'use client';

import { useState } from 'react';
import { toast } from 'sonner';
import { useTranslations } from 'next-intl';
import { authClient } from '@/lib/auth/client';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

// ── SVG icon components ─────────────────────────────────────────

function AppleIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className="size-4 shrink-0"
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className="size-4 shrink-0"
      aria-hidden="true"
    >
      <path d="M11.4 24H0V12.6h11.4V24z" fill="#F25022" />
      <path d="M24 24H12.6V12.6H24V24z" fill="#00A4EF" />
      <path d="M11.4 11.4H0V0h11.4v11.4z" fill="#7FBA00" />
      <path d="M24 11.4H12.6V0H24v11.4z" fill="#FFB900" />
    </svg>
  );
}

// ── Component ───────────────────────────────────────────────────

interface SocialLoginButtonsProps {
  /** Redirect URL after successful OAuth (default: /role-select) */
  callbackURL?: string;
}

export function SocialLoginButtons({
  callbackURL = '/role-select',
}: SocialLoginButtonsProps) {
  const t = useTranslations('common');
  const tProfile = useTranslations('auth.profile');
  const [microsoftLoading, setMicrosoftLoading] = useState(false);

  async function handleMicrosoft() {
    try {
      setMicrosoftLoading(true);
      await authClient.signIn.social({
        provider: 'microsoft',
        callbackURL,
      });
    } catch {
      toast.error(t('errors.networkError'));
    } finally {
      setMicrosoftLoading(false);
    }
  }

  function handleApple() {
    toast.info(t('comingSoon'));
  }

  return (
    <div className="flex flex-col gap-3">
      <Button
        type="button"
        variant="outline"
        className="w-full gap-2 h-11 font-medium border-border hover:border-primary/40 transition-colors"
        onClick={handleMicrosoft}
        disabled={microsoftLoading}
      >
        {microsoftLoading ? <Spinner className="size-4" /> : <MicrosoftIcon />}
        <span>{tProfile('continueWithMicrosoft')}</span>
      </Button>

      <Button
        type="button"
        variant="outline"
        className="w-full gap-2 h-11 font-medium border-border hover:border-muted-foreground/30 transition-colors text-muted-foreground"
        onClick={handleApple}
      >
        <AppleIcon />
        <span>{tProfile('continueWithApple')}</span>
        <span className="ms-auto text-xs text-muted-foreground/60 font-normal">
          {t('comingSoon')}
        </span>
      </Button>
    </div>
  );
}
